import classNames from "classnames";
import { forwardRef, Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";

import Autocomplete from "./Autocomplete";
import DatePicker from "./DatePicker";
import PhoneInput from "./PhoneInput";
import SegmentedToggle from "./SegmentedToggle";
import { toast } from "react-toastify";
import Switch from "react-switch";

// Currency formatting helpers - stores cents, displays dollars with commas
const formatCurrencyDisplay = (value = "") => {
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return "";
  const cents = parseInt(digits, 10);
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const extractCurrencyValue = (displayValue = "") => {
  // Remove all non-digits and convert display to cents
  const digits = displayValue.replace(/\D/g, "");
  return digits;
};

function Form({ fields = [], onSubmit, setIsValid, className, labelOnTop }, ref) {
  const { t } = useTranslation();
  useEffect(() => {
    if (setIsValid) {
      let valid = isValidFn();
      setIsValid(valid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, setIsValid]);

  const isValidFn = (printErrors) => {
    let valid = true;
    // Stop at first error
    fields.forEach((field) => {
      if (field.show !== false && field.required) {
        let isEmpty = !field.value;
        if (Array.isArray(field.value)) {
          isEmpty = field.value.length === 0;
        }

        if (isEmpty) {
          valid = false;
          if (printErrors) {
            toast.error(`${field.label} ${t("common.isRequired")}`);
          }
        }
      }
    });
    return valid;
  };

  const checkSubmit = (e) => {
    if (onSubmit && e.key === "Enter") {
      // check validation
      if (!isValidFn(true)) return;
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <>
      <div
        className={classNames({
          form: true,
          horizontal: labelOnTop,
          [className]: !!className,
        })}
      >
        {fields.map((field, index) => {
          if (field.show === false) return null;
          if (field.type === "content") {
            return <Fragment key={index}>{typeof field.content === "function" ? field.content() : field.content}</Fragment>;
          }

          const showRequiredStar = field.showRequiredStar ?? field.required;
          // Handle fieldRef - can be a function or a ref object
          const setFieldRef = (el) => {
            if (field.fieldRef) {
              if (typeof field.fieldRef === "function") {
                field.fieldRef(el);
              } else if (field.fieldRef && typeof field.fieldRef === "object") {
                field.fieldRef.current = el;
              }
            }
          };

          return (
            <div
              ref={setFieldRef}
              data-field-name={field.name || undefined}
              className={classNames({
                "row-form": true,
                horizontal: labelOnTop,
                "vertical-align": !labelOnTop && field.type !== "textarea",
                [field.className]: field.className,
                required: showRequiredStar,
              })}
              key={index}
            >
              {!field.hideLabel && (
                <label>
                  {field.label}
                  {showRequiredStar && <span className="required-star">*</span>}
                </label>
              )}

              {field.type === "textarea" ? (
                <textarea
                  name={field.name}
                  value={field.value}
                  className={classNames({ empty: field.required && !field.value })}
                  onChange={field.onChange && field.onChange}
                  onKeyDown={checkSubmit}
                  placeholder={field.placeholder}
                  rows={field.rows || 4}
                  disabled={field.disabled}
                  aria-describedby={field.ariaDescribedBy || undefined}
                />
              ) : field.type === "select" ? (
                <select
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange && field.onChange}
                  onKeyDown={checkSubmit}
                  disabled={field.disabled}
                  className={classNames({ empty: field.required && !field.value })}
                  aria-describedby={field.ariaDescribedBy || undefined}
                >
                  {field.options?.map((option) => (
                    <option key={option.key} value={option.key} className={option.notranslate && "notranslate"}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === "autocomplete" ? (
                <Autocomplete
                  name={field.name}
                  initValue={field.initValue}
                  onSelect={field.onSelect && field.onSelect}
                  onChange={field.onChange && field.onChange}
                  data={field.data}
                  disabled={field.disabled}
                  placeholder={field.placeholder}
                  disableLocalFilter={field.disableLocalFilter}
                  loading={field.loading}
                  hideClear={field.hideClear}
                  className={classNames({ empty: field.required && !field.value })}
                  onKeyDown={checkSubmit}
                />
              ) : field.type === "date" ? (
                <DatePicker
                  value={field.value}
                  disabled={field.disabled}
                  onChange={field.onChange}
                  onType={field.onType}
                  onKeyDown={checkSubmit}
                  className={classNames({ empty: field.required && !field.value })}
                  showTodayButton={field.showTodayButton}
                  futureFirst={field.futureFirst}
                  showCalendar={field.showCalendar}
                />
              ) : field.type === "phone" ? (
                <PhoneInput
                  countryCode={field.countryCode}
                  onCountryCodeChange={field.onCountryCodeChange}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={field.disabled}
                  placeholder={field.placeholder}
                  className={classNames({ empty: field.required && !field.value })}
                />
              ) : field.type === "switch" ? (
                <Switch
                  checked={field.value}
                  onChange={field.onChange}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  disabled={field.disabled}
                  height={24}
                  width={45}
                  className={classNames({ empty: field.required && !field.value })}
                />
              ) : field.type === "multipleChoice" ? (
                <div className={"multiple-choice-list" + (field.vertical ? " vertical" : "")}>
                  {field.values?.map((option) => {
                    const fieldId = `${field.name || field.label}-${option.key}`;
                    return (
                      <div key={option.key} className="multiple-choice-option">
                        <input
                          type="checkbox"
                          name={field.name || field.label}
                          id={fieldId}
                          checked={Array.isArray(field.value) && field.value.includes(option.key)}
                          className={classNames({
                            empty: field.required && (!field.value || (Array.isArray(field.value) && field.value.length === 0)),
                          })}
                          onChange={(e) => {
                            let newValue = field.value ? [...field.value] : [];
                            if (e.target.checked) {
                              if (option.key === "none") {
                                newValue = ["none"];
                              } else {
                                newValue = newValue.filter((val) => val !== "none");
                                newValue.push(option.key);
                              }
                            } else {
                              newValue = newValue.filter((val) => val !== option.key);
                            }
                            field.onChange({
                              target: {
                                value: newValue,
                                name: field.name,
                              },
                            });
                          }}
                          disabled={field.disabled}
                        />
                        <label htmlFor={fieldId}>{option.label}</label>
                      </div>
                    );
                  })}
                </div>
              ) : field.type === "singleChoice" ? (
                <div className={"multiple-choice-list" + (field.vertical ? " vertical" : "")}>
                  {field.values?.map((option) => {
                    const fieldId = `${field.name || field.label}-${option.key}`;
                    return (
                      <div key={option.key} className="multiple-choice-option">
                        <input
                          type="radio"
                          name={field.name || field.label}
                          id={fieldId}
                          checked={field.value === option.key}
                          disabled={field.disabled}
                          className={classNames({
                            empty: field.required && !field.value,
                          })}
                          onChange={(e) => {
                            field.onChange({
                              target: {
                                value: option.key,
                                name: field.name,
                              },
                            });
                          }}
                        />
                        <label htmlFor={fieldId}>{option.label}</label>
                      </div>
                    );
                  })}
                </div>
              ) : field.type === "segmentedToggle" ? (
                <SegmentedToggle
                  options={field.values}
                  value={field.value}
                  columns={field.columns}
                  disabled={field.disabled}
                  required={field.required}
                  ariaLabel={field.label}
                  name={field.name}
                  onSelect={(value) => field.onChange({ target: { value, name: field.name } })}
                />
              ) : field.type === "currency" ? (
                <input
                  type="text"
                  name={field.name}
                  inputMode="numeric"
                  value={formatCurrencyDisplay(field.value || "")}
                  onChange={
                    field.onChange &&
                    ((e) =>
                      field.onChange({
                        target: {
                          value: extractCurrencyValue(e.target.value),
                          name: field.name,
                        },
                      }))
                  }
                  placeholder={field.placeholder || "0.00"}
                  onKeyDown={(e) => {
                    if (["-", "+", "e", "E"].includes(e.key)) {
                      e.preventDefault();
                    }
                    checkSubmit(e);
                  }}
                  disabled={field.disabled}
                  className={classNames({
                    empty: field.required && !field.value,
                  })}
                  aria-describedby={field.ariaDescribedBy || undefined}
                />
              ) : (
                <input
                  name={field.name}
                  type={field.type === "number" ? "number" : "text"}
                  value={field.value}
                  onChange={field.onChange && field.onChange}
                  placeholder={field.placeholder}
                  onKeyDown={(e) => {
                    if (field.type === "number" && ["-", "+", "e", "E", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                    checkSubmit(e);
                  }}
                  disabled={field.disabled}
                  className={classNames({
                    empty: field.required && !field.value,
                  })}
                  min={field.type === "number" ? field.min : undefined}
                  max={field.type === "number" ? field.max : undefined}
                  aria-describedby={field.ariaDescribedBy || undefined}
                />
              )}
              {field.hint && <div className="form-hint">{field.hint}</div>}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default forwardRef(Form);
